/**
 * Beta Preview — the employee switch that adds the EXTRA knowledge sources to Copilot (ACOP-140,
 * David, 2026-10-13) and enables the other employee beta features (ACOP-163, David, 2026-11-04).
 *
 * Deliberately free of React so the rules can be unit-tested directly — the same reasoning as
 * utils/nav.ts and utils/landing.ts.
 *
 * ── What it is, and what it is NOT ─────────────────────────────────────────────────────────────
 *
 * Home, Copilot and the Knowledge Base are the LIVE experience for everyone. They are not behind
 * this switch and never were again after ACOP-140 — the switch used to swap the old pages for the new
 * ones (2026-08-28) and no longer does. It is an explicit PREVIEW MODE that enables the employee
 * access features, for two reasons:
 *
 *   1. It switches on the other knowledge sources for employee testing — the internal KB, product
 *      knowledge, support cases, change requests and feature requests (utils/sourcePicker.ts). The RAG
 *      cannot yet combine those reliably, so they stay eliminated unless this is on.
 *   2. Other beta features hang off it. Training (the LMS, the upcoming replacement for AvSight
 *      University) is listed in the menu, and its page opens, only while this is on; the University
 *      link is hidden while it is on (utils/nav.ts). The screen re-renders the moment it flips, with no
 *      reload — the switch is never saved, so a reload would only turn it off again.
 *
 * It is a PREFERENCE, not a security boundary: what a user may SEARCH is decided in AVS OS from the
 * employee role (ACOP-142 / OS-188), so flipping this can only ever narrow or widen a request the
 * server will bound anyway. It is visible to EMPLOYEES ONLY and fails CLOSED.
 *
 * ── It is never saved ──────────────────────────────────────────────────────────────────────────
 *
 * It starts OFF on every load and its state is not persisted anywhere (David, 2026-10-13: "dont even
 * save its state"). The old localStorage key is removed on load so a value left by the previous
 * implementation cannot linger.
 */

/** The localStorage key the previous implementation used. Only ever REMOVED now. */
export const LEGACY_BETA_PREVIEW_KEY = "copilotBetaPreview";

/**
 * Whether this caller may use Beta Preview at all: an EMPLOYEE whose engagement is not denied.
 *
 * Fails CLOSED. A missing, malformed or non-array roles value yields false, and so does a denied
 * employee — `determineRoles` gives that caller ["guest", "employee"], and "employee" is not a tier,
 * so the tier check below is what keeps them out (copilot-api would refuse them anyway).
 */
export function canUseBetaPreview(roles: unknown[]): boolean {
  if (!Array.isArray(roles) || !roles.includes("employee")) return false;
  return roles.some((r) => r === "pending" || r === "standard" || r === "support");
}

/**
 * Whether Beta Preview is actually ON for this caller.
 *
 * Takes the in-memory flag. Only a literal `true` counts, and only for a caller who may use it at
 * all, so a stale flag after a role refresh reveals nothing.
 */
export function isBetaPreviewOn(roles: unknown[], on: unknown): boolean {
  return canUseBetaPreview(roles) && on === true;
}

/**
 * Pages that exist only inside Beta Preview (ACOP-163): the page itself needs the switch on, not just
 * its menu link. The switch always starts OFF, so a reload or a deep link to one of these lands on
 * Home — an employee opens it again by switching preview mode on and using the menu.
 *
 * Every `beta` entry in utils/nav.ts must have its path here (nav.test.ts holds that).
 */
export const PREVIEW_ONLY_ROUTES = ["/learning"];

/** Whether a pathname is one of the preview-only pages (or beneath one). Tolerates a trailing slash. */
export function isPreviewOnlyRoute(pathname: unknown): boolean {
  if (typeof pathname !== "string") return false;
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return PREVIEW_ONLY_ROUTES.some((route) => path === route || path.startsWith(route + "/"));
}

/**
 * Whether the user is on a preview-only page while preview mode is off, and so must be moved to Home.
 *
 * Takes the RESOLVED switch state (isBetaPreviewOn), so only a literal `true` for an employee keeps
 * them there. That covers all three ways in: switching OFF while on the page, a reload, and a deep link.
 */
export function mustLeavePreviewRoute(pathname: unknown, previewOn: unknown): boolean {
  return isPreviewOnlyRoute(pathname) && previewOn !== true;
}

/** Remove the preference the previous implementation saved. Tolerates a browser that refuses storage. */
export function clearLegacyBetaPreview(): void {
  try {
    window.localStorage.removeItem(LEGACY_BETA_PREVIEW_KEY);
  } catch {
    /* Safari private mode and blocked site data THROW; there is nothing to clean up then. */
  }
}
